import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {Alert, Button, Select} from 'antd';
import {toast} from 'react-toastify';
import {
  canModifyDocuments,
  canReviewDocuments,
  formatEnumLabel,
  unlistedDocuments,
  unwrap,
} from 'pages/kybDashboard/kybUtils';
import {useKybDocumentPreview} from 'pages/kybDashboard/useKybDocumentPreview';
import {ReviewDocumentModal} from 'pages/kybDashboard/detail/ReviewDocumentModal';
import {
  ADD_KYC_HUB_DOCUMENT,
  DELETE_KYC_HUB_DOCUMENT,
  KYC_HUB_DOCUMENTS,
  REPLACE_KYC_HUB_DOCUMENT,
  REVIEW_KYC_HUB_DOCUMENT,
  UPDATE_KYC_HUB_DOCUMENT_DETAILS,
} from './resolvers';
import {
  DOCUMENT_STATUSES,
  HUB_PAGE_SIZE,
  PROVENANCE_LABELS,
} from './kycHubUtils';
import {useHubList} from './useHubList';
import {HubDocumentsTable} from './HubDocumentsTable';
import {UploadDocumentModal} from './UploadDocumentModal';
import {EditDocumentDetailsModal} from './EditDocumentDetailsModal';
import {ReasonModal} from './ReasonModal';
import {TogglePill} from './TogglePill';
import styles from './kycHub.module.scss';

const REFETCH = ['KycHubDocuments', 'KycHubActivity'];

/**
 * The documents tab: filters, the paged list and every document action.
 *
 * On a KYB account this is the account's only document list — the rows are
 * the same business-document records `getInstitutionalAccountById` returns, so
 * showing both would list every file twice. `kyb` brings the account's rules:
 * review only while `SUBMITTED`/`UNDER_REVIEW`, replace/archive only while
 * `DRAFT`/`REJECTED`/`MORE_INFO`, and a refetch of the account afterwards,
 * because any document change can flip a checklist row. Edit details (type,
 * name, owner) is the one change open in every status. `kyb.beneficialOwners`
 * are the owners a beneficial owner's ID can be attached to.
 */
export const HubDocuments = ({subject, documentTypes, canUpdate, kyb}) => {
  const list = useHubList(KYC_HUB_DOCUMENTS, 'kycHubDocuments', subject);
  const {openDocument, canOpen, openingId} = useKybDocumentPreview();

  const [uploading, setUploading] = useState(null); // {mode, target}
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [addDocument] = useMutation(ADD_KYC_HUB_DOCUMENT);
  const [replaceDocument] = useMutation(REPLACE_KYC_HUB_DOCUMENT);
  const [deleteDocument, {loading: archiving}] = useMutation(
    DELETE_KYC_HUB_DOCUMENT,
  );
  const [reviewDocument, {loading: reviewing}] = useMutation(
    REVIEW_KYC_HUB_DOCUMENT,
  );
  const [updateDetails, {loading: editing}] = useMutation(
    UPDATE_KYC_HUB_DOCUMENT_DETAILS,
  );
  const beneficialOwners = kyb?.beneficialOwners || [];

  const reviewable = canUpdate && (!kyb || canReviewDocuments(kyb.kybStatus));
  const modifiable = canUpdate && (!kyb || canModifyDocuments(kyb.kybStatus));

  const labels = Object.fromEntries(documentTypes.map(t => [t.code, t.en]));
  const typeLabel = code => labels[code] || formatEnumLabel(code);

  const unlistedIds =
    kyb &&
    new Set(unlistedDocuments(list.rows, kyb.requirements).map(doc => doc.id));

  /** Runs one mutation; true on success. Union errors and network errors are toasted separately. */
  const run = async (mutate, field, input, success) => {
    try {
      const {data} = await mutate({
        variables: {input},
        // The KYB account reloads alongside, so the checklist and the rows'
        // checklist tags change together.
        refetchQueries: kyb
          ? [...REFETCH, 'GetInstitutionalAccountById']
          : REFETCH,
        awaitRefetchQueries: true,
      });
      const {error} = unwrap(data?.[field]);
      if (error) {
        toast.error(error);
        return false;
      }
      toast.success(success);
      return true;
    } catch (error) {
      toast.error('The request failed. Try again.');
      return false;
    }
  };

  const handleRegister = fields =>
    uploading?.mode === 'replace'
      ? run(
          replaceDocument,
          'replaceKycHubDocument',
          {documentId: uploading.target.id, ...fields},
          'New version uploaded. It is pending review.',
        )
      : run(
          addDocument,
          'addKycHubDocument',
          {...subject, ...fields},
          'Document added.',
        );

  const handleArchive = async reason => {
    const done = await run(
      deleteDocument,
      'deleteKycHubDocument',
      {documentId: archiveTarget.id, reason},
      'Document archived.',
    );
    if (done) setArchiveTarget(null);
  };

  const handleReview = async input => {
    const done = await run(
      reviewDocument,
      'reviewKycHubDocument',
      input,
      input.status === 'REJECTED' ? 'Document rejected.' : 'Document accepted.',
    );
    if (done) setReviewTarget(null);
  };

  const handleEditDetails = async input => {
    const done = await run(
      updateDetails,
      'updateKycHubDocumentDetails',
      input,
      'Document details updated.',
    );
    if (done) setEditTarget(null);
  };

  const kybNotice =
    kyb && canUpdate && !reviewable && list.rows.length > 0
      ? kyb.kybStatus === 'DRAFT'
        ? 'This business has not submitted yet, so its files cannot be ruled on.'
        : 'This submission is closed to document review.'
      : null;

  return (
    <div>
      <div className={styles.filterBar}>
        <Select
          allowClear
          placeholder="All types"
          className={styles.filterSelect}
          value={list.filters.documentType}
          onChange={documentType => list.setFilter({documentType})}
          options={documentTypes.map(t => ({value: t.code, label: t.en}))}
        />
        <Select
          allowClear
          placeholder="All statuses"
          className={styles.filterSelect}
          value={list.filters.status}
          onChange={status => list.setFilter({status})}
          options={DOCUMENT_STATUSES.map(value => ({
            value,
            label: formatEnumLabel(value),
          }))}
        />
        <Select
          allowClear
          placeholder="Any source"
          className={styles.filterSelect}
          value={list.filters.provenance}
          onChange={provenance => list.setFilter({provenance})}
          options={Object.entries(PROVENANCE_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <TogglePill
          active={Boolean(list.filters.includeArchived)}
          onChange={includeArchived => list.setFilter({includeArchived})}>
          Show archived and replaced
        </TogglePill>
        {canUpdate && (
          <Button
            type="primary"
            className={styles.pushRight}
            onClick={() => setUploading({mode: 'add'})}>
            Add document
          </Button>
        )}
      </div>

      {list.error && (
        <Alert
          type="error"
          showIcon
          className={styles.alert}
          message={list.error}
        />
      )}
      {kybNotice && (
        <Alert
          type="info"
          showIcon
          className={styles.alert}
          message={kybNotice}
        />
      )}

      <HubDocumentsTable
        documents={list.rows}
        typeLabel={typeLabel}
        unlistedIds={unlistedIds}
        reviewable={reviewable}
        modifiable={modifiable}
        editable={canUpdate}
        onPreview={openDocument}
        canPreview={canOpen}
        previewingId={openingId}
        onReview={setReviewTarget}
        onReplace={target => setUploading({mode: 'replace', target})}
        onArchive={setArchiveTarget}
        onEditDetails={setEditTarget}
        loading={list.loading}
        pagination={{
          current: list.page,
          pageSize: HUB_PAGE_SIZE,
          total: list.total,
          onChange: list.setPage,
          showSizeChanger: false,
          showTotal: total => `${total} document${total === 1 ? '' : 's'}`,
        }}
      />

      <UploadDocumentModal
        open={Boolean(uploading)}
        replacing={uploading?.target}
        subject={subject}
        documentTypes={documentTypes}
        beneficialOwners={beneficialOwners}
        onClose={() => setUploading(null)}
        onRegister={handleRegister}
      />

      <ReasonModal
        open={Boolean(archiveTarget)}
        title="Archive document"
        subtitle={archiveTarget?.freeName || archiveTarget?.fileName}
        description="The file leaves the active list. It stays in the history, with this reason."
        placeholder="e.g. Duplicate upload"
        confirmLabel="Archive document"
        submitting={archiving}
        onClose={() => setArchiveTarget(null)}
        onSubmit={handleArchive}
      />

      <EditDocumentDetailsModal
        open={Boolean(editTarget)}
        document={editTarget}
        subjectType={subject.subjectType}
        documentTypes={documentTypes}
        beneficialOwners={beneficialOwners}
        submitting={editing}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEditDetails}
      />

      <ReviewDocumentModal
        open={Boolean(reviewTarget)}
        document={reviewTarget}
        submitting={reviewing}
        onClose={() => setReviewTarget(null)}
        onSubmit={handleReview}
        {...(!kyb && {
          noteLabel: 'Reviewer note',
          notice:
            'The ruling is recorded in the activity timeline. To change a ruled file, replace it with a new version.',
        })}
      />
    </div>
  );
};
